import fetch from 'isomorphic-fetch';
import { Cookies } from 'react-cookie';
import { canUseDOM, doCookiesWarning } from '@surfline/web-common';

import config from 'config';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';
import { USER_AGENT_KBYG } from './constants';

interface ErrorWithResponse extends Error {
  response?: Response;
  statusCode?: number;
}

interface ErrorReportProps {
  [key: string]: any;
  reponseBody?: any;
  statusCode?: number;
  statusText?: string;
  url?: string;
}

interface FetchOptions extends RequestInit {
  cookies?: Record<string, any>;
}

const checkStatus = (res: Response): Response => {
  if (res.status >= 200 && res.status < 300) {
    return res;
  }

  const error: ErrorWithResponse = new Error(
    res?.statusText ? `Base fetch error: ${res.statusText}` : `Base fetch error`,
  );
  error.response = res;
  error.statusCode = res?.status;

  const propsToSendToNewRelic: ErrorReportProps = {
    reponseBody: res?.body,
    statusCode: res?.status,
    statusText: res?.statusText,
    url: res?.url,
  };

  if (error.statusCode === 503 && res?.url?.includes('kbyg/forecast-content')) {
    reportErrorToNewRelic(error, 'baseFetch', propsToSendToNewRelic, true);
  } else if (error.statusCode >= 500) {
    reportErrorToNewRelic(error, 'baseFetch', propsToSendToNewRelic);
  }

  throw error;
};

/**
 * Parse JSON response
 * @param {Response} response
 */
const parseJSON = (response: Response): Promise<any> => response.json();

/**
 * Get access token from cookies
 * @param {object} [serverSideCookies]
 * @return {string | undefined}
 */
export const getAccessToken = (serverSideCookies?: Record<string, any>): string | undefined => {
  const cookies = new Cookies(canUseDOM ? undefined : serverSideCookies);
  return cookies.get('access_token');
};

/**
 * Base URL for Surfline services API (feed, spots, kbyg, etc.)
 */
export const getBaseURL = (): string => config.servicesURL;

/**
 * Main fetch function for API calls
 * @template T
 * @param {string} path
 * @param {FetchOptions} [options]
 * @returns {Promise<T>}
 */
export const doFetch = <T = any>(path: string, options: FetchOptions = {}): Promise<T> => {
  doCookiesWarning(path, options);
  const cookies = new Cookies(canUseDOM ? undefined : options.cookies);
  const nextApi = ['/api/content/', '/api/wave-pool/'].some((substring) =>
    path.includes(substring),
  );
  const at = cookies.get('access_token');
  if (at) {
    path += path.includes('?') ? `&accesstoken=${at}` : `?accesstoken=${at}`;
  }

  const baseUrl = nextApi ? config.appHost : config.servicesURL;

  const headers = {
    ...options.headers,
    ...(canUseDOM ? {} : { 'user-agent': USER_AGENT_KBYG }),
  };

  return fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  })
    .then(checkStatus)
    .then(parseJSON);
};

/**
 * Create an encoded parameter string from an object
 * @param {Record<string, any>} params
 * @returns {string}
 */
export const createParamString = (params: Record<string, any>): string =>
  Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

/**
 * Create a non-encoded parameter string from an object
 * @param {Record<string, any>} params
 * @returns {string}
 */
export const createDecodedParamString = (params: Record<string, any>): string =>
  Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

/**
 * Fetch from external URL
 * @template T
 * @param {string} url
 * @returns {Promise<T>}
 */
export const doExternalFetch = <T = any>(url: string): Promise<T> =>
  fetch(url).then(checkStatus).then(parseJSON);

/**
 * Fetch JavaScript content using ReadableStream
 * @param {string} path
 * @returns {Promise<string>}
 */
export const doJSFetch = async (path: string): Promise<string> => {
  // https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream
  const result = await fetch(path, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })
    .then((response) => response.body)
    .then((rb) => {
      if (!rb) {
        throw new Error('Response body is null');
      }
      const reader = rb.getReader();
      return new ReadableStream({
        start(controller) {
          function push() {
            reader.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }
              controller.enqueue(value);
              push();
            });
          }

          push();
        },
      });
    })
    .then((stream) => new Response(stream, { headers: { 'Content-Type': 'text/html' } }).text())
    .then((res) => res);

  return result;
};

export default doFetch;
